<template>
  <div>
    <CSDialog v-model="showLogin" persistent>
      <div>
        <q-form ref="loginForm">
          <CSCard :style="getSize">
            <CSCardSection>
              <div class="text-h6 text-pageheadercolor text-center">
                Help A Family
              </div>
            </CSCardSection>

            <CSCardSection class="q-pt-none">
              <CSInput
                v-model="emailAddress"
                autofocus
                label="Email Address"
                @keydown.enter.prevent="onSubmit"
                lazy-rules
                :rules="[(val:string) => emptyOrNullStringCheck(val)]"
              >
                <template #before>
                  <CSIcon name="fa-light fa-envelope" />
                </template>
              </CSInput>

              <CSPasswordInput
                v-model="password"
                label="Password"
                @keydown.enter.prevent="onSubmit"
                lazy-rules
                :rules="[(val:string) => emptyOrNullStringCheck(val)]"
              >
              </CSPasswordInput>
            </CSCardSection>

            <CSCardActions class="text-primary" align="right">
              <!-- <CSButton flat label="Add address" v-close-popup /> -->
              <CSButton flat label="Cancel" @click="router.go(-1)" />
              <CSButton
                flat
                label="Forgot Password"
                @click="dialogState = 'showPasswordReset'"
              />
              <CSAsyncButton label="Login" @click="onSubmit" />
            </CSCardActions>
          </CSCard>
        </q-form>
      </div>
    </CSDialog>

    <CSDialog v-model="showCodeEnter" persistent>
      <div>
        <q-form ref="codeEnterForm">
          <CSCard :style="getSize">
            <CSCardSection>
              <div class="text-h6 text-pageheadercolor text-center">
                Help A Family
              </div>
            </CSCardSection>

            <CSCardSection>
              <p>
                Please check your phone for a code sent by Help A Family and
                enter that code here.
              </p>
              <p class="text-weight-bold text-subtitle1">
                This might take up to 2 minutes to be received.
              </p>
            </CSCardSection>

            <CSCardSection class="q-pt-none">
              <CSInput
                v-model="verificationCode"
                autofocus
                label="Verification Code"
                mask="######"
                @keydown.enter.prevent="onVerify"
                lazy-rules
                :rules="[(val:string) => fixedLength(val, 6, 'Code should be 6 digits')]"
              >
                <template #before>
                  <CSIcon name="fa-light fa-mobile" />
                </template>
              </CSInput>
            </CSCardSection>

            <CSCardActions class="text-primary" align="right">
              <CSButton
                flat
                label="Cancel"
                @click="dialogState = 'showLogin'"
              />
              <CSAsyncButton label="Submit" @click="onVerify" />
            </CSCardActions>
          </CSCard>
        </q-form>
      </div>
    </CSDialog>

    <CSDialog v-model="showPasswordReset" persistent>
      <div>
        <q-form ref="forgotPasswordForm">
          <CSCard :style="getSize">
            <CSCardSection>
              <div class="text-h6 text-pageheadercolor text-center">
                Help A Family
              </div>
            </CSCardSection>

            <CSCardSection class="q-pt-none">
              <div class="text-center">
                Please submit your email address, and a password reset link will
                be sent to you.
              </div>
            </CSCardSection>

            <CSCardSection class="q-pt-none">
              <CSInput
                v-model="emailAddress"
                autofocus
                label="Email Address"
                @keydown.enter.prevent="requestPasswordReset"
                lazy-rules
                :rules="[(val:string)=> emptyOrNullStringCheck(val)]"
              >
                <template #before>
                  <CSIcon name="fa-light fa-envelope" />
                </template>
              </CSInput>
            </CSCardSection>

            <CSCardActions class="text-primary" align="right">
              <CSButton
                flat
                label="Cancel"
                @click="dialogState = 'showLogin'"
              />
              <CSAsyncButton label="Submit" @click="requestPasswordReset" />
            </CSCardActions>
          </CSCard>
        </q-form>
      </div>
    </CSDialog>
  </div>
</template>

<script setup lang="ts">
import CSCard from "@/components/CharitySystem/CSCard.vue";
import CSCardSection from "@/components/CharitySystem/CSCardSection.vue";
import CSCardActions from "@/components/CharitySystem/CSCardActions.vue";
import CSAsyncButton from "@/components/CharitySystem/CSAsyncButton.vue";
import CSButton from "@/components/CharitySystem/CSButton.vue";
import CSInput from "@/components/CharitySystem/CSInput.vue";
import CSIcon from "@/components/CharitySystem/CSIcon.vue";
import CSDialog from "@/components/CharitySystem/CSDialog.vue";
import CSPasswordInput from "@/components/CharitySystem/CSPasswordInput.vue";

import { ref } from "vue";
import { computed } from "vue";
import { useQuasar } from "quasar";
import {
  showErrorNotification,
  showPositiveNotification,
} from "@/components/shared/dialog";
import { emptyOrNullStringCheck, fixedLength } from "@/shared/validations";
import SecurityUseCase from "@/usecases/SecurityUseCase";
import { useStore } from "@/store";
import { useRouter } from "vue-router";
import { processResponse, stringHasValue } from "@/shared/helperFunctions";
import { QForm } from "quasar";
import { ApiException, createApiException } from "@/api/ResponseModel";

const $q = useQuasar();
const store = useStore();
const router = useRouter();
const loginForm = ref(null);
const codeEnterForm = ref(null);
const forgotPasswordForm = ref(null);

const securityUseCase = new SecurityUseCase();

const dialogState = ref("showLogin");
const emailAddress = ref("");
const password = ref("");
const verificationCode = ref("");

// let token = "";

const showLogin = computed(() => dialogState.value === "showLogin");
const showCodeEnter = computed(() => dialogState.value === "showCodeEnter");
const showPasswordReset = computed(
  () => dialogState.value === "showPasswordReset"
);

const getSize = computed(() => {
  return $q.screen.gt.xs ? { minWidth: "500px" } : {};
});

async function onSubmit() {
  //@ts-ignore --> the reference gets its value duuring the setup
  const validate = await loginForm.value.validate();
  if (!validate) return;
  try {
    const result = await securityUseCase.login(
      emailAddress.value,
      password.value
    );
    processResponse(result, (_) => {
      // token = content;
      dialogState.value = "showCodeEnter";
      $q.notify(
        showPositiveNotification(
          "A verification code was sent to your mobile number."
        )
      );
    });
  } catch (ex) {
    $q.notify(showErrorNotification((ex as ApiException).title));
  }
}

async function onVerify() {
  //@ts-ignore --> the reference gets its value duuring the setup
  const validate = await codeEnterForm.value.validate();
  if (!validate) return;
  try {
    const result = await securityUseCase.verifyMFAForLogin(
      verificationCode.value
    );

    processResponse(result, async (content) => {
      store.commit("mutStoreCurrentUser", content.user);

      await store.dispatch("actionLoadReferenceData");
      $q.notify(showPositiveNotification("Successfully logged in"));
      router.replace("/");
    });
  } catch (ex) {
    $q.notify(showErrorNotification((ex as ApiException).title));
  }
}

async function requestPasswordReset() {
  //@ts-ignore --> the reference gets its value duuring the setup
  const validate = await forgotPasswordForm.value.validate();
  if (!validate) return;
  try {
    if (!stringHasValue(emailAddress.value))
      throw createApiException(
        "Email Address Required",
        "Please provide an email address to reset your password."
      );

    const result = await securityUseCase.requestPasswordReset(
      emailAddress.value
    );

    processResponse(result, () => {
      $q.notify(
        showPositiveNotification(
          "An email has been sent with the password reset link."
        )
      );
      dialogState.value = "showLogin";
      emailAddress.value = "";
    });
  } catch (ex) {
    $q.notify(showErrorNotification((ex as ApiException).title));
  }
}
</script>
